/*
 * Collection Filter v2 — Modu Drawer
 * ----------------------------------------------------------------------------
 * A single client-side faceted filter, led by a "fits my drawer" control.
 *
 * One component, hosted responsively (no duplicate markup):
 *   - Desktop (>=1024px): a vertical accordion in the LEFT sidebar
 *     ([data-cf2-aside], rendered by collection-sidebar-navigation.liquid).
 *   - Mobile/tablet (<1024px): a "Filter" button in the content column
 *     ([data-cf2-bar], rendered by main-collection-product-grid.liquid) that
 *     opens a bottom sheet PORTALED to <body> — so theme wrappers (sticky /
 *     transformed / animated) can never trap its position or z-index.
 *
 * The grid paginates, so all pages are loaded in the BACKGROUND via the Section
 * Rendering API (after the UI is already on screen) — the filter never blocks
 * the first paint, and the "fits" filter still sees off-page matches.
 *
 * Default-on for everyone. ?filterv2=0 is an emergency per-browser kill switch.
 * Event delegation lives on `document`, so the UI can be moved between the
 * sidebar, the sheet and <body> without ever re-binding listeners.
 */
(() => {
  'use strict';

  // ---------------- Kill switch (default ON) ----------------
  const FLAG_KEY = 'cf-v2';
  function flagActive() {
    try {
      const q = new URLSearchParams(location.search).get('filterv2');
      if (q === '0' || q === 'off' || q === 'false') { localStorage.setItem(FLAG_KEY, '0'); return false; }
      if (q === '1' || q === 'on' || q === 'true') { localStorage.removeItem(FLAG_KEY); return true; }
      return localStorage.getItem(FLAG_KEY) !== '0';
    } catch (_) { return true; }
  }

  // ---------------- Config ----------------
  const FIT_TOLERANCE_MM = 3;       // slack so an exact-fit product still passes
  const INPUT_DEBOUNCE_MS = 200;
  const SHOW_MORE_AT = 8;           // truncate long option lists past this many
  const MAX_LOAD_PAGES = 30;        // safety cap for background load-all
  const ASIDE_MQ = '(min-width: 1024px)';   // sidebar visible == accordion mode

  const FACETS = ['utensil', 'type'];
  const FACET_LABEL = { utensil: 'Utensil', type: 'Type' };

  // ---------------- State ----------------
  const state = {
    selected: { utensil: new Set(), type: new Set() },
    fit: { width: null, depth: null }, // "depth" key = the LENGTH axis (grid_length); shown as "Length" in the UI to match the drawer builder (length × width)
    products: [],
    valueLookup: { utensil: new Map(), type: new Map() },
    total: 0,
    suppressUrl: false,
  };

  // Built-once UI pieces (moved between hosts/sheet, never recreated).
  const ui = {
    built: false,
    controls: null, chips: null, count: null, asideHeading: null,
    fbtn: null, sheet: null, sheetBody: null, backdrop: null,
  };

  // ---------------- DOM helpers ----------------
  // Desktop host. Prefer the Liquid-declared host; but the sidebar SECTION file
  // can be out of sync with git on the live theme, so if it's absent we inject a
  // host into the existing .sidebar-nav. The JS/CSS assets and the grid section
  // deploy independently, so the desktop filter mounts regardless.
  function asideHost() {
    // Only mount on filterable pages: those render the [data-cf2-bar] host.
    // Curated pages (kitchen) omit it, so the filter never appears there.
    if (!barHost()) return null;
    let h = document.querySelector('[data-cf2-aside]');
    if (h) return h;
    const nav = document.querySelector('.sidebar-nav');
    if (!nav) return null;
    h = document.createElement('div');
    h.setAttribute('data-cf2-aside', '');
    h.hidden = true;
    nav.appendChild(h);
    return h;
  }
  const barHost = () => document.querySelector('[data-cf2-bar]');
  const cfg = () => barHost();   // carries data-cf2-section / data-cf2-url
  const grid = () => document.querySelector('#ProductsList') || document.querySelector('#ProductGridContainer');
  const isAside = () => window.matchMedia(ASIDE_MQ).matches;
  const q = (sel) => document.querySelector(sel);

  function parseList(v) {
    if (!v) return [];
    return v.split('||').map((s) => s.trim()).filter(Boolean);
  }
  function num(v) {
    const n = Number(String(v == null ? '' : v).trim());
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ---------------- Background load-all (defeat pagination) ----------------
  async function ensureAllLoaded() {
    const c = cfg();
    const g = grid();
    if (!c || !g) return;
    const sectionId = c.dataset.cf2Section;
    const baseUrl = c.dataset.cf2Url || location.pathname;
    if (!sectionId) return;

    // Idempotent across re-inits: drop cards a previous run injected.
    g.querySelectorAll('.f-column[data-cf2-injected]').forEach((n) => n.remove());

    // Fetch only when more products exist than are in the DOM — robust across
    // pagination styles (numbered / load-more / infinite), unlike sniffing for
    // a "?page=" link which load-more/infinite don't render.
    const total = parseInt(c.dataset.cf2Total || '0', 10) || 0;
    if (total && g.querySelectorAll('.f-column').length >= total) return;

    const seen = new Set(
      Array.from(g.querySelectorAll('.f-column a[href*="/products/"]'))
        .map((a) => (a.getAttribute('href') || '').split('?')[0])
    );

    for (let page = 2; page <= MAX_LOAD_PAGES; page += 1) {
      const sep = baseUrl.indexOf('?') === -1 ? '?' : '&';
      const url = `${baseUrl}${sep}section_id=${encodeURIComponent(sectionId)}&page=${page}`;
      let html;
      try {
        const res = await fetch(url, { headers: { 'X-Requested-With': 'fetch' } });
        if (!res.ok) break;
        html = await res.text();
      } catch (_) { break; }
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const cards = doc.querySelectorAll('#ProductsList .f-column');
      if (!cards.length) break;
      let appended = 0;
      cards.forEach((card) => {
        const a = card.querySelector('a[href*="/products/"]');
        const key = a ? (a.getAttribute('href') || '').split('?')[0] : '';
        if (key && seen.has(key)) return;
        if (key) seen.add(key);
        const node = document.importNode(card, true);
        node.setAttribute('data-cf2-injected', '1');
        g.appendChild(node);
        appended += 1;
      });
      if (!appended) break;
    }
  }

  // ---------------- Collect product data ----------------
  function collect() {
    const g = grid();
    state.products = [];
    state.valueLookup = { utensil: new Map(), type: new Map() };
    if (!g) return;
    Array.from(g.querySelectorAll('.f-column')).forEach((wrapper) => {
      const card = wrapper.querySelector('.product-card');
      if (!card) return;
      const utensil = parseList(card.dataset.filterUtensils);
      const type = parseList(card.dataset.filterOptions);
      state.products.push({
        el: wrapper,
        utensil, type,
        w: num(card.dataset.fitWidthMm),
        d: num(card.dataset.fitDepthMm),
        utensilLc: utensil.map((s) => s.toLowerCase()),
        typeLc: type.map((s) => s.toLowerCase()),
      });
      utensil.forEach((label) => {
        const v = label.toLowerCase();
        if (!state.valueLookup.utensil.has(v)) state.valueLookup.utensil.set(v, label);
      });
      type.forEach((label) => {
        const v = label.toLowerCase();
        if (!state.valueLookup.type.has(v)) state.valueLookup.type.set(v, label);
      });
    });
    state.total = state.products.length;
  }

  // ---------------- Matching ----------------
  function matchesFacet(p, facet, selected) {
    if (!selected || selected.size === 0) return true;
    const vals = facet === 'utensil' ? p.utensilLc : p.typeLc;
    for (const v of selected) if (vals.includes(v)) return true;
    return false;
  }
  function matchesFit(p, fit) {
    if (fit.width == null && fit.depth == null) return true;
    if (fit.width != null) {
      if (p.w == null) return false;
      if (p.w > fit.width + FIT_TOLERANCE_MM) return false;
    }
    if (fit.depth != null) {
      if (p.d == null) return false;
      if (p.d > fit.depth + FIT_TOLERANCE_MM) return false;
    }
    return true;
  }
  function isVisible(p) {
    return (
      matchesFacet(p, 'utensil', state.selected.utensil) &&
      matchesFacet(p, 'type', state.selected.type) &&
      matchesFit(p, state.fit)
    );
  }
  // Marginal count: products matching if `value` were the only choice on `axis`,
  // holding the other axes at their current selection (Baymard cross-count).
  function countWith(axis, value) {
    let n = 0;
    state.products.forEach((p) => {
      const ok =
        matchesFacet(p, 'utensil', axis === 'utensil' ? new Set([value]) : state.selected.utensil) &&
        matchesFacet(p, 'type', axis === 'type' ? new Set([value]) : state.selected.type) &&
        matchesFit(p, state.fit);
      if (ok) n += 1;
    });
    return n;
  }
  function anyActive() {
    return (
      state.selected.utensil.size + state.selected.type.size > 0 ||
      state.fit.width != null || state.fit.depth != null
    );
  }

  // ---------------- URL + persistence state ----------------
  // URL params keep links shareable/refresh-safe and always win. When a param is
  // absent we hydrate from localStorage so filters survive collection-to-
  // collection navigation: the drawer fit is GLOBAL (a shopper's drawer size is
  // universal); utensil/type are scoped per "section" (data-cf2-scope, sourced
  // from a collection metafield) so a kitchen "type" never lingers in bathroom.
  const PARAM = { utensil: 'cf_utensil', type: 'cf_type', w: 'cf_w', d: 'cf_d' };
  const LS_FIT = 'modu:cf2:fit';
  const LS_FACETS = 'modu:cf2:facets:';
  const LS_V = 1;
  const scopeKey = () => { const c = cfg(); const s = c && c.dataset.cf2Scope; return s ? s.trim() : ''; };
  function lsGet(k) { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch (_) { return null; } }
  function lsSet(k, o) { try { localStorage.setItem(k, JSON.stringify(o)); } catch (_) { /* private mode */ } }
  function lsDel(k) { try { localStorage.removeItem(k); } catch (_) { /* private mode */ } }
  // Hydrate state. Must run AFTER collect() so valueLookup is populated — we use
  // it to drop persisted facet values the current collection doesn't offer.
  function readUrl() {
    const p = new URLSearchParams(location.search);
    state.suppressUrl = true;
    const sc = scopeKey();
    const persisted = sc ? (lsGet(LS_FACETS + sc) || {}) : {};
    ['utensil', 'type'].forEach((axis) => {
      if (p.has(PARAM[axis])) {
        // URL wins (shared/refreshed link).
        state.selected[axis] = new Set(
          (p.get(PARAM[axis]) || '').split(',').map((s) => s.trim().toLowerCase()).filter(Boolean)
        );
      } else {
        // Hydrate from this scope's persisted facets, dropping any value the
        // current collection doesn't actually have (e.g. a bathroom type in kitchen).
        const vals = Array.isArray(persisted[axis]) ? persisted[axis] : [];
        state.selected[axis] = new Set(
          vals.map((s) => String(s).trim().toLowerCase()).filter((v) => state.valueLookup[axis].has(v))
        );
      }
    });
    if (p.has(PARAM.w) || p.has(PARAM.d)) {
      state.fit.width = num(p.get(PARAM.w));
      state.fit.depth = num(p.get(PARAM.d));
    } else {
      const f = lsGet(LS_FIT) || {};
      state.fit.width = num(f.w);
      state.fit.depth = num(f.d);
    }
    state.suppressUrl = false;
  }
  function writeUrl() {
    if (state.suppressUrl) return;
    const p = new URLSearchParams(location.search);
    ['utensil', 'type'].forEach((axis) => {
      const vals = Array.from(state.selected[axis]);
      if (vals.length) p.set(PARAM[axis], vals.join(',')); else p.delete(PARAM[axis]);
    });
    if (state.fit.width != null) p.set(PARAM.w, state.fit.width); else p.delete(PARAM.w);
    if (state.fit.depth != null) p.set(PARAM.d, state.fit.depth); else p.delete(PARAM.d);
    const qs = p.toString();
    try { history.replaceState({}, '', location.pathname + (qs ? `?${qs}` : '') + location.hash); } catch (_) { /* sandboxed */ }

    // Persist for cross-collection continuity: fit globally, facets per scope.
    if (state.fit.width != null || state.fit.depth != null) lsSet(LS_FIT, { w: state.fit.width, d: state.fit.depth, v: LS_V });
    else lsDel(LS_FIT);
    const sc = scopeKey();
    if (sc) {
      const u = Array.from(state.selected.utensil);
      const t = Array.from(state.selected.type);
      if (u.length || t.length) lsSet(LS_FACETS + sc, { utensil: u, type: t, v: LS_V });
      else lsDel(LS_FACETS + sc);
    }
  }

  // ---------------- Markup builders ----------------
  function optionRowsHtml(axis) {
    return Array.from(state.valueLookup[axis].entries())
      .sort((a, b) => a[1].localeCompare(b[1], undefined, { sensitivity: 'base' }))
      .map(([value, label], i) => {
        const checked = state.selected[axis].has(value);
        const count = checked ? '' : countWith(axis, value);
        const hideCls = i >= SHOW_MORE_AT ? ' cf2-opt--overflow' : '';
        return (
          `<li class="cf2-opt${checked ? ' is-checked' : ''}${hideCls}">` +
            `<label class="cf2-opt__label">` +
              `<input type="checkbox" data-cf2-input data-cf2-axis="${axis}" value="${esc(value)}"${checked ? ' checked' : ''}>` +
              `<span class="cf2-opt__check" aria-hidden="true"></span>` +
              `<span class="cf2-opt__name">${esc(label)}</span>` +
              (count === '' ? '' : `<span class="cf2-opt__count">${count}</span>`) +
            `</label>` +
          `</li>`
        );
      }).join('');
  }
  function facetGroupHtml(axis) {
    const n = state.selected[axis].size;
    const optionCount = state.valueLookup[axis].size;
    // Hide single-value facets: a facet with one option is redundant and just
    // clutter — e.g. inside a Kitchen sub-collection every product's Type is
    // "Kitchen", so the Type filter is implied by the category. It reappears
    // automatically once the collection spans >1 value (e.g. All Products once
    // Bathroom exists). (Baymard: never show a one-option filter.)
    if (optionCount <= 1) return '';
    const showMore = optionCount > SHOW_MORE_AT
      ? `<button type="button" class="cf2-group__more" data-cf2-more="${axis}">Show all (${optionCount})</button>` : '';
    return (
      `<div class="cf2-group" data-cf2-group="${axis}">` +
        `<button type="button" class="cf2-trigger" data-cf2-toggle="${axis}" aria-expanded="false">` +
          `<span class="cf2-trigger__label">${FACET_LABEL[axis]}</span>` +
          `<span class="cf2-trigger__badge" data-cf2-badge="${axis}"${n ? '' : ' hidden'}>${n}</span>` +
          `<svg class="cf2-trigger__chev" viewBox="0 0 16 16" aria-hidden="true"><path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>` +
        `</button>` +
        `<div class="cf2-pop" data-cf2-pop="${axis}" role="group" aria-label="${FACET_LABEL[axis]}" hidden>` +
          (optionCount > SHOW_MORE_AT
            ? `<div class="cf2-search"><input type="search" data-cf2-search="${axis}" placeholder="Search ${FACET_LABEL[axis].toLowerCase()}…" aria-label="Search ${FACET_LABEL[axis]}"></div>` : '') +
          `<ul class="cf2-opts" data-cf2-list="${axis}" role="list">${optionRowsHtml(axis)}</ul>` +
          showMore +
          `<div class="cf2-pop__foot"><button type="button" class="cf2-link" data-cf2-clear="${axis}">Clear</button></div>` +
        `</div>` +
      `</div>`
    );
  }
  function fitGroupHtml() {
    const active = state.fit.width != null || state.fit.depth != null;
    const badge = (state.fit.width != null ? 1 : 0) + (state.fit.depth != null ? 1 : 0);
    return (
      `<div class="cf2-group cf2-group--fit" data-cf2-group="fit">` +
        `<button type="button" class="cf2-trigger cf2-trigger--primary" data-cf2-toggle="fit" aria-expanded="false">` +
          `<svg class="cf2-trigger__ico" viewBox="0 0 20 20" aria-hidden="true"><rect x="2.5" y="4.5" width="15" height="11" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M2.5 8.5h15M7 4.5v11" stroke="currentColor" stroke-width="1.4" fill="none"/></svg>` +
          `<span class="cf2-trigger__label">Fits my drawer</span>` +
          `<span class="cf2-trigger__badge" data-cf2-badge="fit"${active ? '' : ' hidden'}>${badge}</span>` +
          `<svg class="cf2-trigger__chev" viewBox="0 0 16 16" aria-hidden="true"><path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>` +
        `</button>` +
        `<div class="cf2-pop cf2-pop--fit" data-cf2-pop="fit" hidden>` +
          `<fieldset class="cf2-fit">` +
            `<legend class="cf2-fit__legend">Your drawer's internal size</legend>` +
            `<div class="cf2-fit__row">` +
              `<label class="cf2-fit__field"><span>Length</span><span class="cf2-fit__inputwrap"><input type="number" inputmode="numeric" min="0" step="1" data-cf2-fit="depth" value="${state.fit.depth != null ? state.fit.depth : ''}" aria-label="Drawer internal length in millimetres"><em>mm</em></span></label>` +
              `<label class="cf2-fit__field"><span>Width</span><span class="cf2-fit__inputwrap"><input type="number" inputmode="numeric" min="0" step="1" data-cf2-fit="width" value="${state.fit.width != null ? state.fit.width : ''}" aria-label="Drawer internal width in millimetres"><em>mm</em></span></label>` +
            `</div>` +
            `<p class="cf2-fit__hint">Enter your drawer's inside measurements — we'll show only organisers that fit.</p>` +
          `</fieldset>` +
          `<div class="cf2-pop__foot"><button type="button" class="cf2-link" data-cf2-clear="fit">Clear</button></div>` +
        `</div>` +
      `</div>`
    );
  }
  function chipsHtml() {
    const chips = [];
    if (state.fit.depth != null) chips.push(`<button type="button" class="cf2-chip" data-cf2-chip="fitd" aria-label="Remove length filter">Fits length ≤ ${state.fit.depth}mm <span aria-hidden="true">×</span></button>`);
    if (state.fit.width != null) chips.push(`<button type="button" class="cf2-chip" data-cf2-chip="fitw" aria-label="Remove width filter">Fits width ≤ ${state.fit.width}mm <span aria-hidden="true">×</span></button>`);
    FACETS.forEach((axis) => {
      state.selected[axis].forEach((v) => {
        const label = state.valueLookup[axis].get(v) || v;
        chips.push(`<button type="button" class="cf2-chip" data-cf2-chip="${axis}:${esc(v)}" aria-label="Remove ${esc(label)} filter">${esc(label)} <span aria-hidden="true">×</span></button>`);
      });
    });
    if (chips.length >= 1) chips.push(`<button type="button" class="cf2-chip cf2-chip--clear" data-cf2-clear-all>Clear all</button>`);
    return chips.join('');
  }

  // ---------------- Build the (once) UI pieces ----------------
  function el(tag, cls) { const e = document.createElement(tag); if (cls) e.className = cls; return e; }
  function controlsHtml() { return fitGroupHtml() + FACETS.map(facetGroupHtml).join(''); }

  function buildPieces() {
    if (ui.built) return;
    ui.controls = el('div', 'cf2-controls');
    ui.controls.innerHTML = controlsHtml();

    ui.chips = el('div', 'cf2-applied');
    ui.chips.setAttribute('data-cf2-applied', '');

    ui.count = el('p', 'cf2-count');
    ui.count.setAttribute('data-cf2-count', '');
    ui.count.setAttribute('role', 'status');
    ui.count.setAttribute('aria-live', 'polite');

    // Subtle "Filter" eyebrow — shown only in the desktop sidebar (aside mode)
    // to signal where the filter section starts. Mobile already has the
    // "Filter" button + sheet header, so this isn't placed there.
    ui.asideHeading = el('p', 'cf2-aside-heading');
    ui.asideHeading.innerHTML =
      `<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3 5h14M5 10h10M8 15h4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>` +
      `<span>Filter</span>`;

    ui.fbtn = el('button', 'cf2-fbtn');
    ui.fbtn.type = 'button';
    ui.fbtn.setAttribute('data-cf2-open-sheet', '');
    ui.fbtn.setAttribute('aria-haspopup', 'dialog');
    ui.fbtn.setAttribute('aria-expanded', 'false');
    ui.fbtn.innerHTML =
      `<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3 5h14M5 10h10M8 15h4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>` +
      `<span>Filter</span><span class="cf2-fbtn__badge" data-cf2-mobile-badge hidden></span>`;

    ui.sheet = el('div', 'cf2-sheet');
    ui.sheet.setAttribute('data-cf2-sheet', '');
    ui.sheet.setAttribute('role', 'dialog');
    ui.sheet.setAttribute('aria-modal', 'true');
    ui.sheet.setAttribute('aria-label', 'Filters');
    ui.sheet.innerHTML =
      `<div class="cf2-sheet__head"><span>Filters</span><button type="button" class="cf2-sheet__close" data-cf2-close-sheet aria-label="Close filters">×</button></div>` +
      `<div class="cf2-sheet__body" data-cf2-sheet-body></div>` +
      `<div class="cf2-sheet__foot"><button type="button" class="cf2-apply" data-cf2-close-sheet>Show <span data-cf2-result-count>${state.total}</span> results</button></div>`;
    ui.sheetBody = ui.sheet.querySelector('[data-cf2-sheet-body]');

    ui.backdrop = el('div', 'cf2-backdrop');
    ui.backdrop.setAttribute('data-cf2-close-sheet', '');
    ui.backdrop.hidden = true;

    ui.built = true;
  }

  // Rebuild the controls' inner markup (after data changes — e.g. load-all
  // surfaced new option values). Keeps the same element node + its location.
  function rebuildControls() {
    if (ui.controls) ui.controls.innerHTML = controlsHtml();
  }

  // ---------------- Placement (responsive, no duplicate markup) ----------------
  function render() {
    const a = asideHost();
    const b = barHost();
    // Desktop uses the sidebar accordion only when a sidebar host exists;
    // collection templates without a sidebar fall back to the button + sheet.
    if (isAside() && a) {
      closeSheet();
      ui.controls.classList.add('cf2-controls--aside');
      ui.controls.classList.remove('cf2-controls--sheet');
      if (a) { a.replaceChildren(ui.asideHeading, ui.controls, ui.chips, ui.count); a.hidden = false; }
      if (b) { b.replaceChildren(); b.hidden = true; }
    } else {
      ui.controls.classList.add('cf2-controls--sheet');
      ui.controls.classList.remove('cf2-controls--aside');
      ui.sheetBody.appendChild(ui.controls);              // controls live in the sheet
      if (b) { b.replaceChildren(ui.fbtn, ui.chips, ui.count); b.hidden = false; }
      if (a) { a.replaceChildren(); a.hidden = true; }
    }
  }

  // ---------------- Sheet (portaled to <body>) ----------------
  function openSheet() {
    if (isAside() && asideHost()) return;   // aside mode (desktop + sidebar): no sheet
    if (ui.sheet.parentNode !== document.body) document.body.appendChild(ui.sheet);
    if (ui.backdrop.parentNode !== document.body) document.body.appendChild(ui.backdrop);
    if (ui.controls.parentNode !== ui.sheetBody) ui.sheetBody.appendChild(ui.controls);
    ui.backdrop.hidden = false;
    if (ui.fbtn) ui.fbtn.setAttribute('aria-expanded', 'true');
    // next frame so the slide-up transition runs from the off-screen start
    window.requestAnimationFrame(() => {
      document.body.classList.add('cf2-sheet-open');
      const closeBtn = ui.sheet.querySelector('[data-cf2-close-sheet]');
      if (closeBtn) { try { closeBtn.focus(); } catch (_) {} }
    });
  }
  function closeSheet() {
    document.body.classList.remove('cf2-sheet-open');
    if (ui.backdrop) ui.backdrop.hidden = true;
    if (ui.fbtn) ui.fbtn.setAttribute('aria-expanded', 'false');
    // return focus to the trigger if focus was inside the sheet
    if (ui.sheet && ui.fbtn && ui.sheet.contains(document.activeElement)) {
      try { ui.fbtn.focus(); } catch (_) {}
    }
  }

  // ---------------- Accordion (aside only) ----------------
  function closeAllPops() {
    if (!ui.controls) return;
    ui.controls.querySelectorAll('.cf2-group').forEach((g) => g.classList.remove('is-open'));
    ui.controls.querySelectorAll('[data-cf2-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    ui.controls.querySelectorAll('[data-cf2-pop]').forEach((p) => { p.hidden = true; });
  }
  function togglePop(axis) {
    if (!isAside()) return;                 // sheet mode: groups always open (CSS)
    if (!ui.controls) return;
    const group = ui.controls.querySelector(`.cf2-group[data-cf2-group="${axis}"]`);
    if (!group) return;
    const open = group.classList.contains('is-open');
    closeAllPops();
    if (!open) {
      group.classList.add('is-open');
      const t = group.querySelector('[data-cf2-toggle]');
      const p = group.querySelector('[data-cf2-pop]');
      if (t) t.setAttribute('aria-expanded', 'true');
      if (p) p.hidden = false;
    }
  }

  // ---------------- In-place refresh (counts / chips / checked) ----------------
  function refresh() {
    if (!ui.controls) return;
    FACETS.forEach((axis) => {
      const list = ui.controls.querySelector(`[data-cf2-list="${axis}"]`);
      if (list) {
        list.querySelectorAll('.cf2-opt').forEach((li) => {
          const input = li.querySelector('[data-cf2-input]');
          if (!input) return;
          const checked = state.selected[axis].has(input.value);
          input.checked = checked;
          li.classList.toggle('is-checked', checked);
          let countEl = li.querySelector('.cf2-opt__count');
          if (checked) {
            if (countEl) countEl.remove();
          } else {
            if (!countEl) {
              countEl = document.createElement('span');
              countEl.className = 'cf2-opt__count';
              const lbl = li.querySelector('.cf2-opt__label');
              if (lbl) lbl.appendChild(countEl);
            }
            countEl.textContent = countWith(axis, input.value);
          }
        });
      }
      const badge = ui.controls.querySelector(`[data-cf2-badge="${axis}"]`);
      if (badge) { const n = state.selected[axis].size; badge.textContent = n; badge.hidden = !n; }
      const group = ui.controls.querySelector(`.cf2-group[data-cf2-group="${axis}"]`);
      if (group) group.classList.toggle('cf2-group--active', state.selected[axis].size > 0);
    });
    const fitBadge = ui.controls.querySelector('[data-cf2-badge="fit"]');
    if (fitBadge) {
      const n = (state.fit.width != null ? 1 : 0) + (state.fit.depth != null ? 1 : 0);
      fitBadge.textContent = n; fitBadge.hidden = !n;
    }
    const fitGroup = ui.controls.querySelector('.cf2-group[data-cf2-group="fit"]');
    if (fitGroup) fitGroup.classList.toggle('cf2-group--active', state.fit.width != null || state.fit.depth != null);
    if (ui.chips) ui.chips.innerHTML = chipsHtml();
  }

  // ---------------- Apply (filter the grid) ----------------
  function apply(opts) {
    let visible = 0;
    state.products.forEach((p) => {
      const show = isVisible(p);
      p.el.style.display = show ? '' : 'none';
      if (show) visible += 1;
    });
    const empty = q('[data-filter-empty]');
    if (empty) empty.hidden = visible !== 0;
    if (ui.count) {
      ui.count.textContent = anyActive() ? `Showing ${visible} of ${state.total}` : `${state.total} products`;
    }
    const rc = q('[data-cf2-result-count]');
    if (rc) rc.textContent = visible;
    const mb = q('[data-cf2-mobile-badge]');
    if (mb) {
      const n = state.selected.utensil.size + state.selected.type.size
        + (state.fit.width != null ? 1 : 0) + (state.fit.depth != null ? 1 : 0);
      mb.textContent = n; mb.hidden = !n;
    }
    if (!opts || !opts.skipRefresh) refresh();
    if (!opts || !opts.skipUrl) writeUrl();
  }

  // ---------------- Interactions ----------------
  let inputTimer = null;
  function onFitInput(input) {
    const which = input.dataset.cf2Fit;
    clearTimeout(inputTimer);
    inputTimer = setTimeout(() => {
      const v = num(input.value);
      state.fit[which] = v;
      apply();
    }, INPUT_DEBOUNCE_MS);
  }

  let wired = false;
  function wireGlobal() {
    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-cf2-toggle]');
      if (toggle && ui.controls && ui.controls.contains(toggle)) { e.preventDefault(); togglePop(toggle.dataset.cf2Toggle); return; }

      const more = e.target.closest('[data-cf2-more]');
      if (more && ui.controls && ui.controls.contains(more)) {
        const list = ui.controls.querySelector(`[data-cf2-list="${more.dataset.cf2More}"]`);
        if (list) list.classList.add('cf2-opts--expanded');
        more.remove();
        return;
      }

      const clear = e.target.closest('[data-cf2-clear]');
      if (clear && ui.controls && ui.controls.contains(clear)) {
        const axis = clear.dataset.cf2Clear;
        if (axis === 'fit') {
          state.fit.width = null; state.fit.depth = null;
          ui.controls.querySelectorAll('[data-cf2-fit]').forEach((i) => { i.value = ''; });
        } else { state.selected[axis].clear(); }
        apply();
        return;
      }

      if (e.target.closest('[data-cf2-clear-all]')) {
        state.selected.utensil.clear(); state.selected.type.clear();
        state.fit.width = null; state.fit.depth = null;
        if (ui.controls) ui.controls.querySelectorAll('[data-cf2-fit]').forEach((i) => { i.value = ''; });
        apply();
        return;
      }

      const chip = e.target.closest('[data-cf2-chip]');
      if (chip) {
        const k = chip.dataset.cf2Chip;
        if (k === 'fitw') { state.fit.width = null; const i = ui.controls && ui.controls.querySelector('[data-cf2-fit="width"]'); if (i) i.value = ''; }
        else if (k === 'fitd') { state.fit.depth = null; const i = ui.controls && ui.controls.querySelector('[data-cf2-fit="depth"]'); if (i) i.value = ''; }
        else {
          const [axis, ...rest] = k.split(':');
          const val = rest.join(':');
          if (state.selected[axis]) state.selected[axis].delete(val);
        }
        apply();
        return;
      }

      if (e.target.closest('[data-cf2-open-sheet]')) { openSheet(); return; }
      if (e.target.closest('[data-cf2-close-sheet]')) { closeSheet(); return; }

      // outside click closes any open accordion popover (aside mode)
      if (ui.controls && !e.target.closest('.cf2-controls')) closeAllPops();
    });

    document.addEventListener('change', (e) => {
      const input = e.target.closest('[data-cf2-input]');
      if (!input) return;
      const axis = input.dataset.cf2Axis;
      if (input.checked) state.selected[axis].add(input.value); else state.selected[axis].delete(input.value);
      apply();
    });

    document.addEventListener('input', (e) => {
      const fit = e.target.closest('[data-cf2-fit]');
      if (fit) { onFitInput(fit); return; }
      const search = e.target.closest('[data-cf2-search]');
      if (search && ui.controls) {
        const axis = search.dataset.cf2Search;
        const v = (search.value || '').trim().toLowerCase();
        ui.controls.querySelectorAll(`[data-cf2-list="${axis}"] .cf2-opt`).forEach((li) => {
          const name = ((li.querySelector('.cf2-opt__name') || {}).textContent || '').toLowerCase();
          li.style.display = !v || name.includes(v) ? '' : 'none';
        });
      }
    });

    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { closeAllPops(); closeSheet(); } });

    window.addEventListener('popstate', () => {
      readUrl();
      if (ui.controls) {
        const w = ui.controls.querySelector('[data-cf2-fit="width"]'); if (w) w.value = state.fit.width != null ? state.fit.width : '';
        const d = ui.controls.querySelector('[data-cf2-fit="depth"]'); if (d) d.value = state.fit.depth != null ? state.fit.depth : '';
      }
      apply({ skipUrl: true });
    });

    // Re-place when crossing the sidebar breakpoint.
    try { window.matchMedia(ASIDE_MQ).addEventListener('change', () => { render(); }); }
    catch (_) { window.addEventListener('resize', () => { render(); }); }
  }

  // ---------------- Init ----------------
  async function init() {
    if (!flagActive()) return;
    if (!barHost() && !asideHost()) return;
    try {
      document.body.classList.add('cf2-filterable');
      // Filterable page: hide the curated featured-collection rows so the grid
      // the filter controls (#ProductsList) is what shoppers actually see. The
      // theme's own swap is template-dependent and doesn't fire on the
      // sub-collection template, so do it reliably here.
      document.querySelectorAll('.section--featured-collection').forEach((el) => {
        const sec = el.closest('.shopify-section');
        if (sec) sec.style.display = 'none';
      });
      collect();
      readUrl();
      if (!ui.built) buildPieces(); else rebuildControls();
      render();                       // UI on screen immediately (no load-all wait)
      if (!wired) { wireGlobal(); wired = true; }
      apply({ skipUrl: true });
      writeUrl();
      // Load the rest of the collection in the background, then refresh data.
      ensureAllLoaded().then(() => {
        const beforeU = state.valueLookup.utensil.size;
        const beforeT = state.valueLookup.type.size;
        collect();
        // Only rebuild the controls markup if new option values appeared;
        // otherwise refresh counts in place so an open accordion / focus / typed
        // search isn't reset under the user.
        if (state.valueLookup.utensil.size !== beforeU || state.valueLookup.type.size !== beforeT) {
          rebuildControls();
        }
        apply({ skipUrl: true });
      }).catch(() => {});
    } catch (e) {
      if (window.console && console.warn) console.warn('[collection-filter-v2] init error:', e);
    }
  }

  function boot() { init(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:render', boot);
  document.addEventListener('shopify:section:reorder', boot);
})();
